// src/components/crm/CustomerTable.js
import React, { useState, useEffect, useRef } from 'react';
import { ArrowUp, ArrowDown, Edit, Trash2, Phone, Mail, SlidersHorizontal } from 'lucide-react';
import CustomerStatusBadge from '../common/CustomerStatusBadge';

const STORAGE_KEY = 'spatchy_customer_table_columns';

const DEFAULT_COLUMNS = {
    company:     true,
    salesPerson: true,
    added:       true,
    ltv:         true,
    status:      true,
};

const COLUMN_LABELS = {
    company:     'Company',
    salesPerson: 'Sales Person',
    added:       'Added',
    ltv:         'LTV',
    status:      'Status',
};

// canViewFinancials gates the LTV column (header, cell, mobile card) and its
// column-picker toggle. Defaults true so any caller that doesn't pass it is
// unaffected; CustomerManagementView passes false for users without
// VIEW_FINANCIALS (e.g. flag-on Field Technicians).
const CustomerTable = ({ customers, openModal, onDeleteRequest, requestSort, sortConfig, canViewFinancials = true }) => {
    const [visibleColumns, setVisibleColumns] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? { ...DEFAULT_COLUMNS, ...JSON.parse(saved) } : DEFAULT_COLUMNS;
        } catch {
            return DEFAULT_COLUMNS;
        }
    });
    const [isColumnPickerOpen, setIsColumnPickerOpen] = useState(false);
    const pickerRef = useRef(null);

    // Persist column visibility to localStorage
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns));
        } catch {}
    }, [visibleColumns]);

    // Close picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setIsColumnPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleColumn = (key) => {
        setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return <span className="inline-block w-4"></span>;
        return sortConfig.direction === 'ascending'
            ? <ArrowUp className="inline-block ml-1 h-4 w-4 text-blue-600" />
            : <ArrowDown className="inline-block ml-1 h-4 w-4 text-blue-600" />;
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
    };

    return (
        <div className="mt-4 sm:mt-6">

            {/* --- DESKTOP VIEW --- */}
            <div className="hidden md:block">

                {/* Column Picker */}
                <div className="flex justify-end mb-2">
                    <div className="relative" ref={pickerRef}>
                        <button
                            onClick={() => setIsColumnPickerOpen(prev => !prev)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${isColumnPickerOpen ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                        >
                            <SlidersHorizontal size={13} />
                            Columns
                        </button>

                        {isColumnPickerOpen && (
                            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[160px]">
                                <p className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100">
                                    Show / Hide
                                </p>
                                {Object.entries(COLUMN_LABELS).filter(([key]) => canViewFinancials || key !== 'ltv').map(([key, label]) => (
                                    <label
                                        key={key}
                                        className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm text-gray-700"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={visibleColumns[key]}
                                            onChange={() => toggleColumn(key)}
                                            className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        {label}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
                    <table className="w-full text-sm text-left text-gray-500">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
                            <tr>
                                {/* Name — always visible */}
                                <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('name')}>
                                    Name {getSortIndicator('name')}
                                </th>

                                {visibleColumns.company && (
                                    <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('company')}>
                                        Company {getSortIndicator('company')}
                                    </th>
                                )}

                                {visibleColumns.salesPerson && (
                                    <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('sales_person_name')}>
                                        Sales Person {getSortIndicator('sales_person_name')}
                                    </th>
                                )}

                                {visibleColumns.added && (
                                    <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('createdAt')}>
                                        Added {getSortIndicator('createdAt')}
                                    </th>
                                )}

                                {visibleColumns.ltv && canViewFinancials && (
                                    <th className="px-6 py-3 text-right cursor-pointer hover:bg-gray-100" onClick={() => requestSort('lifetimeValue')}>
                                        LTV {getSortIndicator('lifetimeValue')}
                                    </th>
                                )}

                                {visibleColumns.status && (
                                    <th className="px-6 py-3 text-center cursor-pointer hover:bg-gray-100" onClick={() => requestSort('status')}>
                                        Status {getSortIndicator('status')}
                                    </th>
                                )}

                                {/* Actions — always visible */}
                                <th className="px-6 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {customers.map(customer => (
                                <tr key={customer.id} className="bg-white hover:bg-blue-50 cursor-pointer transition-colors" onClick={() => openModal(customer)}>
                                    <td className="px-6 py-4 font-medium text-gray-900">{customer.displayName || '—'}</td>

                                    {visibleColumns.company && (
                                        <td className="px-6 py-4 text-gray-600">{customer.company || '-'}</td>
                                    )}

                                    {visibleColumns.salesPerson && (
                                        <td className="px-6 py-4">{customer.sales_person_name || '-'}</td>
                                    )}

                                    {visibleColumns.added && (
                                        <td className="px-6 py-4">{customer.createdAt?.toDate().toLocaleDateString() || 'N/A'}</td>
                                    )}

                                    {visibleColumns.ltv && canViewFinancials && (
                                        <td className="px-6 py-4 text-right font-medium">{formatCurrency(customer.lifetimeValue || 0)}</td>
                                    )}

                                    {visibleColumns.status && (
                                        <td className="px-6 py-4 text-center"><CustomerStatusBadge status={customer.status}/></td>
                                    )}

                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                            <button onClick={() => openModal(customer)} className="p-1.5 hover:bg-blue-100 text-blue-600 rounded"><Edit size={16}/></button>
                                            <button onClick={() => onDeleteRequest(customer.id)} className="p-1.5 hover:bg-red-100 text-red-600 rounded"><Trash2 size={16}/></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {customers.length === 0 && (
                        <div className="text-center py-10 bg-gray-50">
                            <p className="text-gray-500">No customers found.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* --- MOBILE CARD VIEW --- */}
            <div className="md:hidden space-y-3 pb-20">
                {customers.map(customer => (
                    <div
                        key={customer.id}
                        onClick={() => openModal(customer)}
                        className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 active:scale-[0.99] transition-transform cursor-pointer relative"
                    >
                        <div className="flex justify-between items-start mb-1">
                            <h3 className="text-lg font-bold text-gray-900 truncate pr-2">{customer.displayName || '—'}</h3>
                            <CustomerStatusBadge status={customer.status} />
                        </div>

                        {customer.company && (
                            <p className="text-sm text-gray-500 mb-2">{customer.company}</p>
                        )}

                        <div className="grid grid-cols-2 gap-2 mb-3 text-sm text-gray-600">
                            <div className="flex flex-col">
                                <span className="text-xs text-gray-400">Sales Person</span>
                                <span className="font-medium">{customer.sales_person_name || 'Unassigned'}</span>
                            </div>
                            {canViewFinancials && (
                                <div className="flex flex-col text-right">
                                    <span className="text-xs text-gray-400">Lifetime Value</span>
                                    <span className="font-bold text-green-600">{formatCurrency(customer.lifetimeValue || 0)}</span>
                                </div>
                            )}
                        </div>

                        <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
                            <div className="flex gap-3">
                                {customer.phone && (
                                    <a href={`tel:${customer.phone}`} onClick={(e) => e.stopPropagation()} className="text-gray-400 hover:text-blue-600">
                                        <Phone size={18} />
                                    </a>
                                )}
                                {customer.email && (
                                    <a href={`mailto:${customer.email}`} onClick={(e) => e.stopPropagation()} className="text-gray-400 hover:text-blue-600">
                                        <Mail size={18} />
                                    </a>
                                )}
                            </div>

                            <button
                                onClick={(e) => { e.stopPropagation(); onDeleteRequest(customer.id); }}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                            >
                                <Trash2 size={18} />
                            </button>
                        </div>
                    </div>
                ))}

                {customers.length === 0 && (
                    <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                        <p className="text-gray-500">No customers found.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomerTable;